import { useEffect, useState } from 'react';
import { orgsApi } from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';
import { Loader2, QrCode, Copy, Trash2, Plus, Building2, Inbox, Check, X } from 'lucide-react';
import { useConfirm } from './ConfirmProvider';

export function InviteOrgDialog({ open, onOpenChange }) {
  const confirm = useConfirm();
  const [invitations, setInvitations] = useState([]);
  const [accessRequests, setAccessRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [actingId, setActingId] = useState(null);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [expires, setExpires] = useState(30);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await orgsApi.listInvitations();
      setInvitations(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load invitations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [inv, req] = await Promise.all([
          orgsApi.listInvitations(),
          orgsApi.listAccessRequests('pending'),
        ]);
        if (!cancelled) {
          setInvitations(inv.data || []);
          setAccessRequests(req.data || []);
        }
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.detail || 'Failed to load invitations');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const { data } = await orgsApi.createInvitation({
        suggested_org_name: newName.trim() || undefined,
        contact_email: newEmail.trim() || undefined,
        expires_in_days: expires,
      });
      setInvitations((prev) => [data, ...prev]);
      setSelected(data);
      setNewName('');
      setNewEmail('');
      toast.success('Invitation created');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create invitation');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (id) => {
    if (!(await confirm({ title: 'Revoke this invitation?', confirmText: 'Revoke', destructive: true }))) return;
    try {
      await orgsApi.revokeInvitation(id);
      setInvitations((prev) => prev.map((i) => (i.id === id ? { ...i, status: 'revoked' } : i)));
      toast.success('Invitation revoked');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to revoke');
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const handleApprove = async (req) => {
    setActingId(req.id);
    try {
      const { data } = await orgsApi.approveAccessRequest(req.id);
      setAccessRequests((prev) => prev.filter((r) => r.id !== req.id));
      setInvitations((prev) => [data, ...prev]);
      setSelected(data);
      toast.success(`Invitation emailed to ${req.contact_email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve');
    } finally {
      setActingId(null);
    }
  };

  const handleDecline = async (req) => {
    if (!(await confirm({ title: `Decline access request from ${req.org_name}?`, confirmText: 'Decline', destructive: true }))) return;
    setActingId(req.id);
    try {
      await orgsApi.declineAccessRequest(req.id);
      setAccessRequests((prev) => prev.filter((r) => r.id !== req.id));
      toast.success('Request declined');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to decline');
    } finally {
      setActingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-slate-900 text-white border-slate-700">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <Building2 className="w-5 h-5 text-teal-400" /> Invite an organisation
          </DialogTitle>
          <DialogDescription className="text-slate-300">
            Generate a one-time QR code for a new NDIS provider to set up their own
            isolated Visitex workspace, or approve a pending access request below.
          </DialogDescription>
        </DialogHeader>

        {accessRequests.length > 0 && (
          <div className="bg-amber-500/10 border border-amber-400/40 rounded-md p-3 space-y-2" data-testid="access-requests-panel">
            <div className="flex items-center gap-2 text-amber-200 font-medium">
              <Inbox className="w-4 h-4" />
              {accessRequests.length} pending access {accessRequests.length === 1 ? 'request' : 'requests'}
            </div>
            <div className="space-y-2 max-h-48 overflow-auto">
              {accessRequests.map((req) => (
                <div key={req.id} className="flex items-start justify-between gap-2 bg-slate-900/60 border border-slate-700 rounded px-3 py-2" data-testid={`access-request-${req.id}`}>
                  <div className="min-w-0 flex-1 text-sm">
                    <div className="font-medium text-white truncate">{req.org_name}</div>
                    <div className="text-slate-400 text-xs">
                      {req.contact_name} · <a href={`mailto:${req.contact_email}`} className="text-teal-300 underline">{req.contact_email}</a>
                      {req.contact_phone ? ` · ${req.contact_phone}` : ''}
                    </div>
                    {req.ndis_registration_number && (
                      <div className="text-xs text-slate-500 mt-0.5">NDIS: {req.ndis_registration_number}</div>
                    )}
                    {req.message && (
                      <div className="text-xs text-slate-300 mt-1 italic">&ldquo;{req.message}&rdquo;</div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <Button
                      size="sm"
                      onClick={() => handleApprove(req)}
                      disabled={actingId === req.id}
                      className="bg-emerald-600 hover:bg-emerald-700 h-7 px-2"
                      data-testid={`approve-access-${req.id}`}
                    >
                      {actingId === req.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Check className="w-3 h-3 mr-1" />Approve</>}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDecline(req)}
                      disabled={actingId === req.id}
                      className="text-rose-600 h-7 px-2"
                      data-testid={`decline-access-${req.id}`}
                    >
                      <X className="w-3 h-3 mr-1" />Decline
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <form onSubmit={handleCreate} className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end" data-testid="invite-org-form">
          <div className="space-y-1">
            <Label className="text-slate-300">Org name (optional)</Label>
            <Input
              data-testid="invite-org-name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Acme Care"
              className="bg-slate-800 border-slate-600"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-slate-300">Contact email (optional)</Label>
            <Input
              data-testid="invite-org-email"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="hello@acme.com"
              className="bg-slate-800 border-slate-600"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-slate-300">Expires in (days)</Label>
            <div className="flex gap-2">
              <Input
                data-testid="invite-org-expires"
                type="number" min={1} max={365}
                value={expires}
                onChange={(e) => setExpires(Number(e.target.value) || 30)}
                className="bg-slate-800 border-slate-600"
              />
              <Button
                type="submit" disabled={creating}
                data-testid="invite-org-create-btn"
                className="bg-teal-600 hover:bg-teal-700"
              >
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Create</>}
              </Button>
            </div>
          </div>
        </form>

        {selected && (
          <div className="bg-slate-800/60 border border-teal-500/30 rounded-md p-4 flex flex-col sm:flex-row gap-4" data-testid="invite-org-qr-preview">
            <img src={selected.qr_data_url} alt="QR code" className="w-44 h-44 rounded-md bg-white p-2" />
            <div className="space-y-2 flex-1 min-w-0">
              <div className="text-xs text-slate-400">Signup link</div>
              <div className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-sm text-teal-200 break-all">
                {selected.signup_url}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => copy(selected.signup_url)} className="text-slate-900">
                  <Copy className="w-3 h-3 mr-1" /> Copy link
                </Button>
                <a
                  href={selected.qr_data_url}
                  download={`visitex-invite-${selected.code}.png`}
                  className="inline-flex items-center px-3 py-1.5 text-sm rounded-md bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="invite-org-download-qr"
                >
                  <QrCode className="w-3 h-3 mr-1" /> Download QR
                </a>
              </div>
              <div className="text-xs text-slate-400">
                Code: <span className="font-mono text-slate-200">{selected.code}</span> •
                Expires: <span className="text-slate-200">{new Date(selected.expires_at).toLocaleDateString('en-AU', { timeZone: 'Australia/Sydney' })}</span>
              </div>
            </div>
          </div>
        )}

        <div className="mt-2 space-y-2 max-h-64 overflow-auto" data-testid="invite-org-list">
          {loading && (
            <div className="flex items-center text-slate-400"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…</div>
          )}
          {!loading && invitations.length === 0 && (
            <div className="text-sm text-slate-400">No invitations yet. Create one above to onboard a new organisation.</div>
          )}
          {!loading && invitations.map((inv) => (
            <div
              key={inv.id}
              className="flex items-center justify-between gap-3 bg-slate-800/50 border border-slate-700 rounded px-3 py-2"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-mono text-teal-300">{inv.code}</span>
                  <span className="text-slate-300 truncate">{inv.suggested_org_name || '(no name)'}</span>
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    inv.status === 'active' ? 'bg-emerald-500/20 text-emerald-300' :
                    inv.status === 'used' ? 'bg-slate-500/20 text-slate-300' :
                    'bg-rose-500/20 text-rose-300'
                  }`}>{inv.status}</span>
                </div>
                <div className="text-xs text-slate-400 truncate">
                  Expires {new Date(inv.expires_at).toLocaleDateString('en-AU', { timeZone: 'Australia/Sydney' })}
                  {inv.used_by_email ? ` • Used by ${inv.used_by_email}` : ''}
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button size="sm" variant="outline" onClick={() => setSelected(inv)} className="text-slate-900">
                  <QrCode className="w-3 h-3" />
                </Button>
                {inv.status === 'active' && (
                  <Button size="sm" variant="outline" onClick={() => handleRevoke(inv.id)} className="text-rose-600">
                    <Trash2 className="w-3 h-3" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
